import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const exportMyData = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    const [profileRes, generationsRes, subsRes] = await Promise.all([
      supabase.from("profiles").select("*").eq("user_id", userId).maybeSingle(),
      supabase.from("generations").select("*").eq("user_id", userId).order("created_at", { ascending: false }),
      supabase.from("subscriptions").select("*").eq("user_id", userId),
    ]);

    return {
      exported_at: new Date().toISOString(),
      user_id: userId,
      disclaimer:
        "This export contains all personal data we hold about you in the SmartEssay application. Limited billing records retained by our payment processor (Stripe) for tax/accounting compliance are not included here.",
      profile: profileRes.data ?? null,
      generations: generationsRes.data ?? [],
      subscriptions: subsRes.data ?? [],
    };
  });

export const getAccountStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const [profileRes, generationsRes, subsRes, recentGensRes, blocksRes] = await Promise.all([
      supabase.from("profiles").select("full_name, email, created_at").eq("user_id", userId).maybeSingle(),
      supabase.from("generations").select("id", { count: "exact", head: true }),
      supabase
        .from("subscriptions")
        .select("status, price_id, current_period_end, cancel_at_period_end, environment, updated_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(5),
      supabase
        .from("generations")
        .select("id, title, assignment_type, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(5),
      supabase
        .from("checkout_block_log")
        .select("id, created_at, attempted_price_id, block_reason, environment, existing_status")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(5),
    ]);
    return {
      profile: profileRes.data ?? null,
      generationCount: generationsRes.count ?? 0,
      subscriptions: subsRes.data ?? [],
      recentGenerations: recentGensRes.data ?? [],
      checkoutBlocks: blocksRes.data ?? [],
    };
  });

export const deleteAllGenerations = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { error, count } = await supabase
      .from("generations")
      .delete({ count: "exact" })
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true, count: count ?? 0 };
  });

export const deleteAccount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        confirmation: z.literal("DELETE"),
        acknowledge: z.literal(true),
        reason: z.string().max(500).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { userId, claims } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const email = (claims?.email as string | undefined) ?? null;

    // 1. Audit log (insert before deletion so we keep a record even if later steps fail)
    const { data: logRow, error: logErr } = await supabaseAdmin
      .from("account_deletion_log")
      .insert({ user_id: userId, email, reason: data.reason ?? null })
      .select("id")
      .single();
    if (logErr) throw new Error(`Audit log failed: ${logErr.message}`);

    // 2. Delete user-owned data. Subscriptions are intentionally retained for billing/tax records.
    const { error: genErr } = await supabaseAdmin
      .from("generations")
      .delete()
      .eq("user_id", userId);
    if (genErr) throw new Error(`Failed to delete essays: ${genErr.message}`);

    const { error: profErr } = await supabaseAdmin
      .from("profiles")
      .delete()
      .eq("user_id", userId);
    if (profErr) throw new Error(`Failed to delete profile: ${profErr.message}`);

    // 3. Delete the auth user
    const { error: authErr } = await supabaseAdmin.auth.admin.deleteUser(userId);
    if (authErr) throw new Error(`Failed to delete account: ${authErr.message}`);

    // 4. Mark audit complete
    await supabaseAdmin
      .from("account_deletion_log")
      .update({ completed_at: new Date().toISOString() })
      .eq("id", logRow.id);

    return { ok: true };
  });
